import { faClose, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import InputField from "../../components/InputField/InputField"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import type { CreateTimeClockAdjustment } from "../../Objects/TimeClock/CreateTimeClockAdjustment"
import type { TimeClockEntry } from "../../Objects/TimeClock/TimeClockEntry"
import type { TimeClockAdjustmentRequestType } from "../../Objects/TimeClock/TimeClockAdjustmentRequestType"
import type { TimeClockDirectionType } from "../../Objects/TimeClock/TimeClockDirectionType"
import { companyMemory } from "../../Objects/Memory/Memory"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"
import { convertRestaurantLocalToUtcIso, formatDateTimeInTimeZone } from "../../utils/timeZone"

interface TimeClockAdjustmentPopupProps {
  entries: TimeClockEntry[]
  employees?: { employeeId: string; employeeName: string }[]
  isManagement?: boolean
  onClose: () => void
  onSave: (request: CreateTimeClockAdjustment) => Promise<boolean>
}

export const TimeClockAdjustmentPopup: React.FC<TimeClockAdjustmentPopupProps> = ({
  entries,
  employees,
  isManagement = false,
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation(["timeClock", "general", "helpPopup"])

  const [requestedType, setRequestedType] = useState<TimeClockAdjustmentRequestType>("Create")

  const [targetEntryId, setTargetEntryId] = useState<string>("")

  const [requestedDate, setRequestedDate] = useState("")

  const [requestedTime, setRequestedTime] = useState("")

  const [requestedDirection, setRequestedDirection] = useState<TimeClockDirectionType>("In")

  const [employeeId, setEmployeeId] = useState(employees?.[0]?.employeeId ?? "")

  const [reason, setReason] = useState("")

  const [isSaving, setIsSaving] = useState(false)

  const needsTarget = requestedType !== "Create"

  const needsData = requestedType !== "Delete"

  const selectableEntries = entries.filter((entry) => entry.status !== "Rejected")

  const handleSubmit = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      await onSave({
        requestedType,
        targetEntryId: needsTarget && targetEntryId ? Number(targetEntryId) : null,
        requestedOccurredAtUtc: needsData
          ? convertRestaurantLocalToUtcIso(requestedDate, requestedTime, companyMemory.timeZoneId)
          : null,
        requestedDirection: needsData ? requestedDirection : null,
        reason,
        employeeId: isManagement ? employeeId : null,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("timeClock:adjustment.title")}
              icon={faPenToSquare}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-adjustment"
                    title={t("helpPopup:global.timeClockAdjustment.title")}
                    description={t("helpPopup:global.timeClockAdjustment.description")}
                    bullets={t("helpPopup:global.timeClockAdjustment.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-3 p-2"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void handleSubmit()
              }}
            >
              {isManagement && employees && employees.length > 0 && (
                <GenericDropDown
                  initialState={employeeId}
                  data={employees.map((employee) => ({
                    label: employee.employeeName,
                    value: employee.employeeId,
                  }))}
                  handlerFuncion={(event) => setEmployeeId(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.dropdown.employee"
                />
              )}

              <GenericDropDown
                initialState={requestedType}
                data={[
                  { label: t("timeClock:adjustment.type_create"), value: "Create" },
                  { label: t("timeClock:adjustment.type_update"), value: "Update" },
                  { label: t("timeClock:adjustment.type_delete"), value: "Delete" },
                ]}
                handlerFuncion={(event) => setRequestedType(event.target.value as TimeClockAdjustmentRequestType)}
                descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.dropdown.type"
              />

              {needsTarget && (
                <GenericDropDown
                  initialState={targetEntryId}
                  data={[
                    { label: t("timeClock:adjustment.select_target"), value: "" },
                    ...selectableEntries.map((entry) => ({
                      label: `${
                        entry.direction === "In"
                          ? t("timeClock:common.direction_in")
                          : t("timeClock:common.direction_out")
                      } · ${formatDateTimeInTimeZone(entry.occurredAtUtc, i18n.language, companyMemory.timeZoneId)}`,
                      value: String(entry.id),
                    })),
                  ]}
                  handlerFuncion={(event) => setTargetEntryId(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.dropdown.target"
                />
              )}

              {needsData && (
                <>
                  <InputField
                    type="date"
                    value={requestedDate}
                    required
                    placeholder={t("timeClock:adjustment.requested_date")}
                    onChange={(event) => setRequestedDate(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.input.requestedDate"
                  />

                  <InputField
                    type="time"
                    value={requestedTime}
                    required
                    placeholder={t("timeClock:adjustment.requested_time")}
                    onChange={(event) => setRequestedTime(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.input.requestedTime"
                  />

                  <Animate type={AnimationTypes.fadeIn} duration="1.5s">
                    <p className="text-xs text-slate-500">{t("timeClock:adjustment.time_zone_note")}</p>
                  </Animate>

                  <div className={defaultStyle.divisionBar}></div>

                  <GenericDropDown
                    initialState={requestedDirection}
                    data={[
                      { label: t("timeClock:common.direction_in"), value: "In" },
                      { label: t("timeClock:common.direction_out"), value: "Out" },
                    ]}
                    handlerFuncion={(event) => setRequestedDirection(event.target.value as TimeClockDirectionType)}
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.dropdown.direction"
                  />
                </>
              )}

              <InputField
                type="text"
                value={reason}
                required
                maxLength={500}
                placeholder={t("timeClock:adjustment.reason_placeholder")}
                onChange={(event) => setReason(event.target.value)}
                descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.input.reason"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  typeButton="submit"
                  label={isManagement ? t("timeClock:adjustment.submit_management") : t("timeClock:adjustment.submit")}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  iconColor={THEME.colors.iconBGSecondaryColor}
                  disabled={isSaving}
                  descriptionKey="interactionDescriptions:pages.timeClock.timeClockAdjustmentPopup.button.submit"
                />
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
